/**
 * Data layer for the drive Home / Projects pages.
 *
 * Project listings come from the workspace API (`/api/workspaces*`) via raw
 * `request` calls — NOT an `@papan/workspace` import — mirroring DriveView's
 * pattern, so `@papan/files` gains no package dependency on the workspace
 * feature (which already depends on files; an import would be circular).
 */
import { useQuery } from '@tanstack/react-query'
import { request, getActiveOrgEmail, getActiveTenantId } from '@papan/api-client'
import { stripProjectFolderExt, isProjectFolder, isMiniappFolder, isSharedPointer, stripPointerSuffix } from '@papan/types'
import { nameFromEmail } from '@papan/core-ui/ui'

/**
 * Per-project access summary — the drive Access-column vocabulary, served by
 * `GET /api/workspaces`. Grantee lists (teams/people) are manager-gated
 * server-side (empty for non-managers); floor/links/outsiders are coarse and
 * present for everyone. See docs/plans/2026-07-14_(completed)_PROJECTS_MINIAPPS_VIEW_REVAMP.md.
 */
export interface ProjectAccessSummary {
  scope: 'org' | 'personal'
  floor?: 'viewer' | 'member' | 'admin' | 'owner'
  locked?: boolean
  /** Manager-gated — empty for a caller without `manage_folder_permission`.
   *  Use `viaMyTeam` to ask whether the VIEWER is reached through their team. */
  teams: Array<{ name: string; isMine: boolean }>
  teamCount: number
  /** Sent to everyone: is the viewer granted this via a team they belong to? */
  viaMyTeam?: boolean
  /** Grantee emails (manager-gated). */
  people: string[]
  peopleCount: number
  links: number
  outsiders: number
  canManage: boolean
}

// ─── Shared overview (the Shared page) ────────────────────────────────────────

export interface IncomingShare {
  id: string
  name: string
  parentId: string | null
  updatedAt?: string | null
  sourceTenantId: string | null
  sourceTenantName: string | null
  broken: boolean
  /** What the SOURCE org granted this drive (readcopy/readwrite/admin) — the badge. */
  permission?: string
  /** The CALLER's own power over the local mount row (owner/admin here). Gates
   *  the management verbs — distinct from `permission`, which is the source's
   *  grant and reads readcopy even for a local org owner. */
  canManage?: boolean
  /** Who in THIS drive can see the mount — the footer facepile. Present only
   *  when the caller manages the row (grantee faces are manager-gated). */
  access?: ProjectAccessSummary | null
  /** When the (latest) grant behind this mount was given. */
  sharedAt?: string | null
  isFolder?: boolean
  itemCount?: number
}

export interface OutgoingShare {
  id: string
  folderId: string
  folderName: string
  recipientTenantId: string
  recipientTenantName: string | null
  permission: string
  createdAt?: string | null
  canManage: boolean
  /** Folder-LIKE (a real folder, or a `.shared`/`.chain` mount artifact). */
  isFolder?: boolean
  /** Full VFS path of the shared folder — drives the Location column. */
  path?: string | null
  /** The folder's parent — reveal opens the PARENT and highlights the folder.
   *  A `papan_`-prefixed value (or null) means it sits at the drive root. */
  parentId?: string | null
}

/** `GET /api/files/shared-overview` — the Shared page, and the Shared place in the drive pickers. */
export function useSharedOverview(
  asAdmin: boolean,
  { refetchOnMount = 'always', enabled = true }: { refetchOnMount?: boolean | 'always'; enabled?: boolean } = {},
) {
  return useQuery<{ incoming: IncomingShare[]; outgoing: OutgoingShare[] }>({
    queryKey: ['shared-overview', getActiveTenantId(), getActiveOrgEmail(), asAdmin],
    queryFn: () => request(`/api/files/shared-overview${asAdmin ? '?asAdmin=1' : ''}`),
    staleTime: 30_000,
    // `FileListCacheSync` marks this stale on a `.shared` file event, a grant
    // change (`org:access_changed`) and a resync, whatever page is open
    // (#1058). The page still refetches on every mount: not every change that
    // reaches it rides one of those signals (the source org renaming or
    // emptying what it shared, for one), and the page is a ROUTE inside the
    // drive deck, so a mount is the natural "look again". staleTime still
    // dedupes in-page churn. (A picker, which remounts its list on every hop,
    // opts down to `true`.)
    refetchOnMount,
    enabled,
  })
}

/** Shape of `/api/workspaces` + `/api/workspaces/recent` rows. */
export interface ProjectItem {
  id: string
  folderId: string
  name: string
  /** Full VFS path, e.g. `/papan/Persons/a@b.c/Projects/Foo.project` */
  path: string
  updatedAt?: string
  createdAt?: string
  /** Creator ≈ owner (created_by actor). Absent on some legacy rows. */
  owner?: { name: string; email?: string }
  access?: ProjectAccessSummary
  /** Server-computed "Show as admin" decision — hidden when the toggle is off. */
  hiddenWithoutAdminView?: boolean
  /** D118 (owner 2026-10-20): this app's (or this) databases.json can't be read — its databases are held back. Only for
   *  whoever can edit the app. `path` = the manifest from the app; `manifestRowId` opens it. */
  manifestProblem?: { path: string; reason: string; manifestRowId: string }
  /** Work shared in whose source is gone (revoked or deleted) — `useIncomingSharedProjects` rows only. */
  broken?: boolean
  /**
   * The linked folder row's `metadata_json`, stripped to the `miniappIcon`
   * key by the `/api/workspaces*` endpoints (set only for `.miniapp` rows).
   * Absent → mini-app cards render the name-derived default icon.
   */
  metadataJson?: string | null
}

function identityKey(): readonly [string | null, string | null] {
  return [getActiveTenantId(), getActiveOrgEmail()] as const
}

export const projectKeys = {
  list: () => ['projects', 'list', ...identityKey()] as const,
  recent: () => ['projects', 'recent', ...identityKey()] as const,
}

/**
 * All projects the viewer can read (server permission-filters for orgs).
 *
 * Realtime: `FileListCacheSync` (mounted for the whole session) marks this list
 * stale when a project / `.miniapp` folder changes — created, restored,
 * re-iconed, renamed, deleted — and on a resync (#1058;
 * `derived-cache-sync.ts`). It used to subscribe here, so only while a view
 * reading it was mounted: a project deleted while you were on Settings stayed
 * on Home until the 30 s `staleTime` ran out.
 */
export function useProjects(enabled = true) {
  return useQuery<ProjectItem[]>({
    queryKey: projectKeys.list(),
    queryFn: async () => {
      const r = await request<{ workspaces: ProjectItem[] }>('/api/workspaces')
      return r.workspaces
    },
    staleTime: 30_000,
    enabled,
  })
}

/**
 * Projects and apps another organisation shared IN, as `ProjectItem`s.
 *
 * Sourced from `/api/files/shared-overview` — the Shared page's endpoint —
 * rather than `/api/workspaces`. That endpoint walks the folder tree, and an
 * inbound share is a POINTER row (`is_folder = false`), so it cannot appear
 * there no matter what the name predicate says; the walk would have to grow a
 * second row type, resolve the source tenant, and find a permission tree that
 * is not local. `shared-overview` already does all of that.
 *
 * Why the page needs them at all: accepting a share into the drive should put
 * the work where work lives. The Shared page is an inbox — it answers "what
 * arrived" — and Projects answers "what do I have". A project that is only ever
 * findable in the inbox is one the recipient has to remember the origin of.
 */
export function useIncomingSharedProjects(enabled = true) {
  return useQuery<ProjectItem[]>({
    queryKey: ['projects', 'incoming-shared', ...identityKey()],
    queryFn: async () => {
      const r = await request<{ incoming?: IncomingShareRow[] }>('/api/files/shared-overview')
      return incomingSharedProjects(r.incoming ?? [])
    },
    staleTime: 30_000,
    enabled,
  })
}

/**
 * The incoming rows of `shared-overview` that are projects or apps, as
 * `ProjectItem`s — shared by the Projects page's query and the drive pickers
 * (which already hold the Shared list's copy of that response).
 */
export function incomingSharedProjects(rows: readonly IncomingShareRow[]): ProjectItem[] {
  return rows
    // Only entity pointers. The endpoint returns every inbound mount,
    // including plain folders and files, which this page does not list.
    .filter(row => {
      const inner = stripPointerSuffix(row.name)
      return isSharedPointer(row.name) && (isProjectFolder(inner) || isMiniappFolder(inner))
    })
    .map(row => ({
      id: `ws_${row.id}`,
      folderId: row.id,
      name: row.name,
      // No path: it is a mount, so it has no place in the viewer's own
      // hierarchy — and an empty one keeps `isMyProject` false, which is
      // the right answer for work that belongs to another organisation.
      path: '',
      updatedAt: row.updatedAt ?? undefined,
      ...(row.access ? { access: row.access } : {}),
      ...(row.sourceTenantName ? { owner: { name: row.sourceTenantName } } : {}),
      ...(row.broken ? { broken: true } : {}),
    }))
}

/** The `shared-overview` incoming row, narrowed to what this page reads. */
interface IncomingShareRow {
  id: string
  name: string
  broken?: boolean
  updatedAt?: string | null
  sourceTenantName?: string | null
  access?: ProjectAccessSummary | null
}

/** Recently OPENED projects (per-email for orgs), server-limited to 10. */
export function useRecentProjects(enabled = true) {
  return useQuery<ProjectItem[]>({
    queryKey: projectKeys.recent(),
    queryFn: async () => {
      const r = await request<{ workspaces: ProjectItem[] }>('/api/workspaces/recent')
      return r.workspaces
    },
    staleTime: 30_000,
    enabled,
  })
}

/**
 * "Mine" = the project lives in the viewer's personal container — the new
 * `Persons/<email>/…` hierarchy or the legacy `_system/workspaces/users/<email>/…`
 * one (Stage 4 backfill hasn't landed, so both shapes are live). Personal
 * tenants own everything by definition.
 */
export function isMyProject(path: string, orgEmail: string | null | undefined, isOrg: boolean): boolean {
  if (!isOrg) return true
  if (!orgEmail) return false
  const p = path.toLowerCase()
  const email = orgEmail.toLowerCase()
  return p.includes(`/persons/${email}/`) || p.includes(`/users/${email}/`)
}

/** Display name: folder name without the `.project`/legacy/`.miniapp` ext. */
export function projectDisplayName(name: string): string {
  return stripProjectFolderExt(name)
}

/**
 * Deterministic hue from the project name — gives every project a stable,
 * recognizable tile color without any stored metadata.
 */
export function projectHue(name: string): number {
  let h = 0
  for (let i = 0; i < name.length; i++) h = ((h * 31) + name.charCodeAt(i)) >>> 0
  return h % 360
}

// ─── Sorting (Projects / MiniApps page) ──────────────────────────────────────

export type ProjectSort = 'modified' | 'created' | 'name'

/** A stable comparator for the chosen sort. Name is A→Z; dates newest-first. */
export function sortProjects(items: ProjectItem[], sort: ProjectSort): ProjectItem[] {
  const byName = (a: ProjectItem, b: ProjectItem) =>
    projectDisplayName(a.name).localeCompare(projectDisplayName(b.name), undefined, { sensitivity: 'base' })
  const byDate = (key: 'createdAt' | 'updatedAt') => (a: ProjectItem, b: ProjectItem) => {
    const cmp = (b[key] ?? '').localeCompare(a[key] ?? '')
    return cmp !== 0 ? cmp : byName(a, b)
  }
  const cmp = sort === 'name' ? byName : sort === 'created' ? byDate('createdAt') : byDate('updatedAt')
  return [...items].sort(cmp)
}

// ─── Org-section classification ───────────────────────────────────────────────

export type ProjectSection = 'mySpace' | 'myTeams' | 'org' | 'outsider'

/**
 * Which collapsible org section a project belongs to, relative to the viewer:
 *   - mySpace  — lives in the viewer's personal `Persons/<myEmail>/` space
 *   - myTeams  — granted to one of the viewer's own teams
 *   - outsider — shared out to / in from an outside org (has outsider exposure)
 *   - org      — everything else in the organization
 * First match wins in that order. Personal tenants have no sections.
 */
export function classifyProjectSection(
  project: ProjectItem,
  orgEmail: string | null | undefined,
): ProjectSection {
  if (isMyProject(project.path, orgEmail, true)) return 'mySpace'
  // `viaMyTeam`, not `teams.some(t => t.isMine)`. The team LIST is
  // manager-gated, so for a member without `manage_folder_permission` it
  // arrives empty and this test answered "no" for every project — including
  // the ones their own team was granted. The boolean is computed from the same
  // data server-side and sent to everyone, because it describes the caller
  // rather than the grant list.
  if (project.access?.viaMyTeam) return 'myTeams'
  // INBOUND only — a `.shared` pointer is work another organisation sent here.
  //
  // `outsiders` is deliberately NOT consulted, and used to be the whole test.
  // It counts shares this drive sent OUT (the summary's query is
  // `owner_tenant_id = <me> AND granted_to_type = 'tenant'`), so the section
  // held the viewer's own outbound shares — the exact opposite of what it
  // says — and a project moved out of the section its placement had earned the
  // moment it was shared onward. Sharing a project outward does not change
  // whose it is.
  if (isSharedPointer(project.name)) return 'outsider'
  return 'org'
}

// ─── Facet filter (team / role / member / creator) ────────────────────────────

export type FacetType = 'team' | 'role' | 'member' | 'creator'

export interface Facet {
  type: FacetType
  /** Stable value used for equality (team name, role floor, email). */
  value: string
  /** Human label for the dropdown. */
  label: string
}

/** The access-floor filter option a project falls under. 'admin' and 'owner'
 *  floors are one option, "Limited" — like the drive's locked label, never
 *  "Admins only" / "Owner only": the Drive Admin role may be cut too (#787 §3). */
export type FloorFacet = 'viewer' | 'member' | 'limited'
export function floorFacet(floor: string | undefined | null): FloorFacet | null {
  if (floor === 'viewer' || floor === 'member') return floor
  if (floor === 'admin' || floor === 'owner') return 'limited'
  return null
}
const FLOOR_ORDER: Record<FloorFacet, number> = { viewer: 0, member: 1, limited: 2 }
const DEFAULT_FLOOR_LABEL: Record<FloorFacet, string> = { viewer: 'Viewer+', member: 'Member+', limited: 'Limited' }

/** Distinct facet values present across `items`, grouped for the filter dropdown.
 *  `floorLabel` renders the access-floor options (the view passes its translations). */
export function collectFacets(
  items: ProjectItem[],
  floorLabel: (floor: FloorFacet) => string = (f) => DEFAULT_FLOOR_LABEL[f],
): Record<FacetType, Facet[]> {
  const teams = new Map<string, Facet>()
  const roles = new Map<string, Facet>()
  const members = new Map<string, Facet>()
  const creators = new Map<string, Facet>()
  for (const p of items) {
    for (const t of p.access?.teams ?? []) {
      if (!teams.has(t.name)) teams.set(t.name, { type: 'team', value: t.name, label: t.name })
    }
    const floor = floorFacet(p.access?.floor)
    if (floor && !roles.has(floor)) roles.set(floor, { type: 'role', value: floor, label: floorLabel(floor) })
    for (const email of p.access?.people ?? []) {
      if (!members.has(email)) members.set(email, { type: 'member', value: email, label: nameFromEmail(email) })
    }
    if (p.owner) {
      const val = p.owner.email ?? p.owner.name
      if (!creators.has(val)) creators.set(val, { type: 'creator', value: val, label: p.owner.name || nameFromEmail(val) })
    }
  }
  const sortByLabel = (a: Facet, b: Facet) => a.label.localeCompare(b.label)
  return {
    team: [...teams.values()].sort(sortByLabel),
    role: [...roles.values()].sort((a, b) => FLOOR_ORDER[a.value as FloorFacet] - FLOOR_ORDER[b.value as FloorFacet]),
    member: [...members.values()].sort(sortByLabel),
    creator: [...creators.values()].sort(sortByLabel),
  }
}

/** Whether a project matches the selected facet. */
export function projectMatchesFacet(project: ProjectItem, facet: Facet): boolean {
  const a = project.access
  switch (facet.type) {
    case 'team': return !!a?.teams?.some(t => t.name === facet.value)
    case 'role': return floorFacet(a?.floor) === facet.value
    case 'member': return !!a?.people?.includes(facet.value)
    case 'creator': return (project.owner?.email ?? project.owner?.name) === facet.value
  }
}

/**
 * Merge recently-opened (ordered first) with the full list (newest-modified
 * first) into the Home "Suggested" strip, deduped by folderId.
 */
export function suggestProjects(recent: ProjectItem[], all: ProjectItem[], cap = 8): ProjectItem[] {
  // `recent` rows are thin (no access/owner); prefer the enriched `all` entry.
  const allById = new Map(all.map(p => [p.folderId, p]))
  const seen = new Set<string>()
  const out: ProjectItem[] = []
  const byModified = [...all].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
  for (const p of [...recent, ...byModified]) {
    const item = allById.get(p.folderId) ?? p
    if (seen.has(item.folderId)) continue
    seen.add(item.folderId)
    out.push(item)
    if (out.length >= cap) break
  }
  return out
}
